import React, { useEffect, useState } from "react";
import { Close } from "@material-ui/icons";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import dayjs from "~/config/dayjs";
import useDateFormatter from "~/hooks/useDateFormatter";

const BRAND = "#672E37";
const LINE_COLOR = "#EBEBEB";
const INK = "#1A1A1E";
const BODY = "#555";
const MUT = "#888";
const TINT = "#FEF2F4";
const TINT_BD = "#F0C4CC";
// Tempo de espera pela mensagem antes de oferecer outra saída
const DELAY_MS = 20000;

function maskPhone(phone) {
	if (!phone) return null;
	const digits = phone.replace(/\D/g, "");
	const local = digits.startsWith("55") ? digits.slice(2) : digits;
	if (local.length < 6) return null;
	const ddd = local.slice(0, 2);
	const last4 = local.slice(-4);
	return "(" + ddd + ") \u2022\u2022\u2022\u2022\u2022\u2022-" + last4;
}

export default function ModalValidarTelefone({
	open,
	onClose,
	// Fechamento automático depois do sucesso; sem ele, usa onClose
	onAutoClose,
	services = [],
	scheduledDate,
	servicesScheduled = [],
	currentStep = 3,
	totalSteps = 5,
	onLoginWithWhatsApp,
	authStatus = "PROCESS",
	isLoading = false,
	isAuthenticated = false,
	phoneNumber,
	onFallback,
}) {
	const { t, i18n } = useTranslation();
	const { formatShortTime } = useDateFormatter();
	const [animationKey, setAnimationKey] = useState(0);
	const [hasSentMessage, setHasSentMessage] = useState(false);
	const [isDelayed, setIsDelayed] = useState(false);

	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
			setAnimationKey((prev) => prev + 1);
			setHasSentMessage(false);
			setIsDelayed(false);
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [open]);

	// Auto-close after showing success state for 1.8 s
	useEffect(() => {
		const reached = authStatus === "SUCCESS" || isAuthenticated;
		const close = onAutoClose || onClose;
		if (reached && open && close) {
			const timer = setTimeout(close, 1800);
			return () => clearTimeout(timer);
		}
	}, [authStatus, isAuthenticated, open]);

	// A mensagem não chegou a tempo: troca a espera por uma saída
	useEffect(() => {
		if (!hasSentMessage || authStatus === "SUCCESS" || isAuthenticated) return;
		const timer = setTimeout(() => setIsDelayed(true), DELAY_MS);
		return () => clearTimeout(timer);
	}, [hasSentMessage, authStatus, isAuthenticated]);

	if (!open) return null;

	const isSuccess = authStatus === "SUCCESS" || isAuthenticated;
	const showLoading = hasSentMessage && !isSuccess && !isDelayed;
	const showDelayed = hasSentMessage && !isSuccess && isDelayed;

	// O dayjs só tem o locale pt-br carregado; nos outros idiomas usa Intl
	const isPortuguese = (i18n.language || "").toLowerCase().startsWith("pt");
	const dateFmt = scheduledDate
		? isPortuguese
			? dayjs(scheduledDate).locale("pt-br").format("DD/MMM").replace(".", "").toLowerCase()
			: new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }).format(new Date(scheduledDate))
		: null;

	const maskedPhone = maskPhone(phoneNumber);

	const handleSend = async () => {
		setHasSentMessage(true);
		setIsDelayed(false);
		if (onLoginWithWhatsApp) await onLoginWithWhatsApp();
	};

	return (
		<>
			{/* Backdrop */}
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(0,0,0,0.5)",
					zIndex: 1299,
					animation: "fadeInMVT 0.3s ease-out",
				}}
			/>

			{/* Bottom sheet */}
			<div
				key={animationKey}
				style={{
					position: "fixed",
					bottom: 0,
					left: 0,
					right: 0,
					width: "100%",
					maxWidth: 480,
					margin: "0 auto",
					backgroundColor: "#fff",
					borderTopLeftRadius: 24,
					borderTopRightRadius: 24,
					padding: "20px 20px 32px",
					boxShadow: "0px -4px 20px rgba(0,0,0,0.15)",
					zIndex: 1300,
					animation: "slideUpMVT 0.3s ease-out",
				}}
			>
				{/* Close button */}
				<div
					onClick={onClose}
					style={{
						position: "absolute",
						top: 16,
						right: 16,
						width: 32,
						height: 32,
						borderRadius: "50%",
						backgroundColor: "rgba(0,0,0,0.05)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
					}}
				>
					<Close style={{ fontSize: 18, color: "#666" }} />
				</div>

				{/* Step badge */}
				<div
					style={{
						paddingBottom: 14,
						marginBottom: 16,
						borderBottom: `1px solid ${LINE_COLOR}`,
					}}
				>
					<span
						style={{
							display: "inline-block",
							background: INK,
							color: "#fff",
							borderRadius: 999,
							padding: "5px 12px",
							fontSize: 12,
							fontWeight: 700,
						}}
					>
						{t("phone_validation_step", { current: currentStep, total: totalSteps })}
					</span>
				</div>

				{/* Service list */}
				{services.length > 0 && (
					<div
						style={{
							padding: "0 2px 16px",
							marginBottom: 18,
							borderBottom: `1px solid ${LINE_COLOR}`,
						}}
					>
						{services.map((svc, i) => {
							const scheduled = servicesScheduled[i];
							const timeStr =
								scheduled?.time?.start
									? formatShortTime(scheduled.time.start)
									: null;
							const profName = scheduled?.professional?.name || null;
							const svcName = svc?.name || svc?.serviceName || "";

							return (
								<div key={svc?.id || i} style={{ marginTop: i === 0 ? 0 : 10 }}>
									<div
										style={{
											fontSize: 14,
											fontWeight: 600,
											color: INK,
											lineHeight: 1.35,
										}}
									>
										{svcName}
									</div>
									{(dateFmt || timeStr || profName) && (
										<div style={{ fontSize: 12.5, color: MUT, marginTop: 2 }}>
											{dateFmt && timeStr
												? t("phone_validation_date_time", { date: dateFmt, time: timeStr })
												: dateFmt || timeStr || ""}
											{profName ? ` · ${t("phone_validation_with_professional", { name: profName })}` : ""}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}

				{isSuccess ? (
					/* ── Success state ── */
					<div
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 12,
							marginTop: 20,
						}}
					>
						<div
							style={{
								width: 28,
								height: 28,
								borderRadius: "50%",
								background: "#22C55E",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								marginTop: 1,
							}}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
								<path
									d="M5 13l4 4L19 7"
									stroke="#fff"
									strokeWidth="2.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
						<div>
							<div style={{ fontSize: 15, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
								{t("phone_validation_success_title")}
							</div>
							{maskedPhone && (
								<div style={{ fontSize: 13, color: MUT, marginTop: 2 }}>
									{maskedPhone}
								</div>
							)}
							<div style={{ fontSize: 13, color: MUT, marginTop: 4, lineHeight: 1.45 }}>
								{t("phone_validation_success_body")}
							</div>
						</div>
					</div>
				) : showLoading ? (
					/* ── Loading / validating state ── */
					<div
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 14,
							marginTop: 20,
						}}
					>
						<CircularProgress
							size={22}
							style={{ color: BRAND, flexShrink: 0, marginTop: 2 }}
						/>
						<div>
							<div
								style={{
									fontSize: 15,
									fontWeight: 700,
									color: INK,
									lineHeight: 1.3,
								}}
							>
								{t("phone_validation_loading_title")}
							</div>
							<div
								style={{
									fontSize: 13,
									color: MUT,
									marginTop: 4,
									lineHeight: 1.45,
								}}
							>
								{t("phone_validation_loading_body")}
							</div>
						</div>
					</div>
				) : showDelayed ? (
					/* ── Delayed state ── */
					<div style={{ marginTop: 20 }}>
						<div style={{ fontSize: 15, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
							{t("phone_validation_delayed_title")}
						</div>
						<div style={{ fontSize: 13, color: MUT, marginTop: 4, lineHeight: 1.45 }}>
							{t("phone_validation_delayed_body")}
						</div>
						<div style={{ display: "flex", gap: 10, marginTop: 18 }}>
							<button
								onClick={onFallback}
								style={{
									flex: 1,
									background: "#F2EDE6",
									border: "none",
									color: INK,
									borderRadius: 999,
									padding: "13px 0",
									fontSize: 14,
									fontWeight: 700,
									cursor: "pointer",
									fontFamily: "inherit",
								}}
							>
								{t("phone_validation_other_way")}
							</button>
							<button
								onClick={handleSend}
								style={{
									flex: 1,
									background: BRAND,
									border: "none",
									color: "#fff",
									borderRadius: 999,
									padding: "13px 0",
									fontSize: 14,
									fontWeight: 700,
									cursor: "pointer",
									fontFamily: "inherit",
								}}
							>
								{t("phone_validation_open_whatsapp")}
							</button>
						</div>
					</div>
				) : (
					/* ── Default state ── */
					<>
						{/* Title */}
						<div
							style={{
								fontSize: 17,
								fontWeight: 800,
								color: INK,
								letterSpacing: -0.3,
								lineHeight: 1.3,
								paddingRight: 26,
							}}
						>
							{t("phone_validation_title")}
						</div>

						{/* Subtitle */}
						<div
							style={{
								fontSize: 13.5,
								color: BODY,
								lineHeight: 1.45,
								margin: "6px 0 14px",
							}}
						>
							{t("phone_validation_subtitle")}
						</div>

						{/* Info box */}
						<div
							style={{
								display: "flex",
								alignItems: "flex-start",
								gap: 9,
								background: TINT,
								border: `1px solid ${TINT_BD}`,
								borderRadius: 12,
								padding: "11px 12px",
								marginBottom: 16,
							}}
						>
							<svg
								width="17"
								height="17"
								viewBox="0 0 24 24"
								fill="none"
								style={{ flexShrink: 0, marginTop: 1 }}
							>
								<path
									d="M9 5.5L3.5 11 9 16.5"
									stroke={BRAND}
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M3.5 11h11a6 6 0 016 6v1.5"
									stroke={BRAND}
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							<div style={{ fontSize: 13, color: INK, lineHeight: 1.45 }}>
								{t("phone_validation_return_hint")}
							</div>
						</div>

						{/* WhatsApp button */}
						<button
							onClick={handleSend}
							style={{
								width: "100%",
								background: BRAND,
								border: "none",
								color: "#fff",
								borderRadius: 999,
								padding: "15px 0",
								fontSize: 15,
								fontWeight: 800,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 10,
								fontFamily: "inherit",
							}}
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
								<path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2z" />
							</svg>
							{t("phone_validation_open_and_send")}
						</button>

						{/* Fallback link */}
						<div style={{ textAlign: "center", marginTop: 20 }}>
							<span
								onClick={onFallback}
								style={{
									fontSize: 13.5,
									color: INK,
									textDecoration: "underline",
									lineHeight: 1.45,
									cursor: "pointer",
								}}
							>
								{t("phone_validation_other_way")}
							</span>
						</div>
					</>
				)}
			</div>

			<style>{`
				@keyframes slideUpMVT {
					from { transform: translateY(100%); opacity: 0; }
					to   { transform: translateY(0);    opacity: 1; }
				}
				@keyframes fadeInMVT {
					from { opacity: 0; }
					to   { opacity: 1; }
				}
			`}</style>
		</>
	);
}
